import { useQuery } from '@apollo/react-hooks'
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonTitle,
  IonToolbar
} from '@ionic/react'
import dayjs from 'dayjs'
import { home } from 'ionicons/icons'
import moment from 'moment'
import React, { useState } from 'react'
import { connect } from "react-redux"
import { RouteComponentProps } from "react-router"
import LoadingScreen from '../../components/loading.screen'
import Note from "../../components/note"
import { pdfDownloadAction } from '../../components/pdf.download'
import { QuoteSignatureModal } from '../../components/quote.signature.modal'
import { QUOTE_QUERY, QuoteData, QuoteVariables } from '../../graphql/query/quote'
import {
  QUOTE_DOWNLOAD_QUERY,
  QuoteDownloadData,
  QuoteDownloadVariables
} from '../../graphql/query/quote.download'
import {
  QUOTE_SIGNATURE_DOWNLOAD_QUERY,
  QuoteSignatureDownloadData,
  QuoteSignatureDownloadVariables
} from '../../graphql/query/quote.signature.download'
import { client } from '../../lib/client'
import { State } from '../../store'
import { addNotification } from '../../store/notification'

interface StateProps {
  accessKey: string
}

type Props = StateProps &  RouteComponentProps<{id: string}>
/*{
  address: any;
  person: any;
  building: any;
  additional: {
    pool: boolean
    horses: any
    dogs: any
    people: Person[]
  };
  gf: string
  addNotification: addNotificationType
}*/

const ListEntry: React.FC<Props> = (props) => {
  const [finalize, setFinalize] = useState(false)

  const {data, loading} = useQuery<QuoteData, QuoteVariables>(QUOTE_QUERY, {
    variables: {
      id: props.match.params.id,
      accessKey: props.accessKey,
    },
  })

  if (loading) {
    return <LoadingScreen message={'Daten werden übertragen'} />
  }

  if (!data || data.quote.status === 'error') {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar color={'primary'}>
            <IonButtons slot="start">
              <IonButton onClick={() => props.history.push({ pathname: '/home' })}>
                <IonIcon slot="icon-only" icon={home} />
              </IonButton>
            </IonButtons>
            <IonTitle>Eigenheim Versicherung</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          Das Angebot konnte nicht aufgerufen werden.
        </IonContent>
      </IonPage>
    )
  }

  if (!data.quote.gf) {
    return <LoadingScreen message={'Angebot wird noch verarbeitet, bitte haben sie geduld!'} />
  }

  const downloadAntrag = async () => {
    const response = await client.query<QuoteDownloadData, QuoteDownloadVariables>({
      query: QUOTE_DOWNLOAD_QUERY,
      variables: {
        id: props.match.params.id,
        accessKey: props.accessKey || 'legacy',
      }
    })

    await pdfDownloadAction(
      response.data.base64,
      `${data.quote.gf}.pdf`
    )
  }

  const download = async () => {
    const response = await client.query<QuoteSignatureDownloadData, QuoteSignatureDownloadVariables>({
      query: QUOTE_SIGNATURE_DOWNLOAD_QUERY,
      variables: {
        id: props.match.params.id,
        accessKey: props.accessKey,
      }
    })

    await pdfDownloadAction(
      response.data.base64,
      `${data.quote.gf}.pdf`
    )
  }

  const {person, address, building, additional} = data.quote

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color={'primary'}>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/list" />
          </IonButtons>
          <IonTitle>Geschäftsfall {data.quote.gf}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Allgemeines</IonCardTitle>
          </IonCardHeader>

          <IonItem>
            <IonLabel position="floating">Geschäftsfall</IonLabel>
            <IonInput value={data.quote.gf} readonly />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">Angelegt</IonLabel>
            <IonInput value={dayjs(data.quote.created).format('YYYY-MM-DD')} readonly />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">Gültig bis</IonLabel>
            <IonInput value={dayjs(data.quote.created).add(6, 'week').format('YYYY-MM-DD')} readonly />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">E-Mail</IonLabel>
            <IonInput value={data.quote.person.email} readonly />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">Mobil</IonLabel>
            <IonInput value={data.quote.person.mobile} readonly />
          </IonItem>

          <IonItem>
            <IonLabel>Angebot als PDF</IonLabel>
            <IonButton fill="outline" slot="end" onClick={() => download()}>Anzeigen</IonButton>
          </IonItem>
          {data.quote.status === 'signed' && (
            <IonItem>
              <IonLabel>Signierter Antrag als PDF</IonLabel>
              <IonButton fill="outline" slot="end" onClick={() => downloadAntrag()}>Anzeigen</IonButton>
            </IonItem>
          )}
        </IonCard>

        {data.quote.status === 'wait_for_signature' && (
          <div style={{ padding: 8 }}>
            <IonButton size={'large'} expand={'block'} onClick={() => setFinalize(true)}>
              Jetzt Unterschreiben
            </IonButton>

            <QuoteSignatureModal
              visible={finalize}
              onHide={() => setFinalize(false)}
              id={data.quote.id}
              gf={data.quote.gf}
            />
          </div>
        )}

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Personen Daten</IonCardTitle>
          </IonCardHeader>

          {person.title && (
            <IonItem>
              <IonLabel position="floating">Titel</IonLabel>
              <IonInput value={person.title} readonly />
            </IonItem>
          )}

          <IonItem>
            <IonLabel position="floating">Vorname</IonLabel>
            <IonInput value={person.firstName} readonly />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">Nachname</IonLabel>
            <IonInput value={person.lastName} readonly />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">Geschlecht</IonLabel>
            <IonInput value={person.gender === 'f' ? 'Frau' : 'Mann'} readonly />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">Geburtstag</IonLabel>
            <IonInput value={moment(person.birthday).format('YYYY-MM-DD')} readonly />
          </IonItem>
        </IonCard>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Adresse</IonCardTitle>
          </IonCardHeader>

          <IonItem>
            <IonLabel position="floating">Straße</IonLabel>
            <IonInput value={address.street} readonly />
          </IonItem>

          {address.house && (
            <IonItem>
              <IonLabel position="floating">Hausnummer</IonLabel>
              <IonInput value={address.house} readonly/>
            </IonItem>
          )}

          {address.stairs && (
            <IonItem>
              <IonLabel position="floating">Stiege</IonLabel>
              <IonInput value={address.stairs} readonly/>
            </IonItem>
          )}

          {address.door && (
            <IonItem>
              <IonLabel position="floating">Türe</IonLabel>
              <IonInput value={address.door} readonly/>
            </IonItem>
          )}

          <IonItem>
            <IonLabel position="floating">PLZ</IonLabel>
            <IonInput value={address.zipCode} readonly />
          </IonItem>

          <IonItem>
            <IonLabel position="floating">Ort</IonLabel>
            <IonInput value={address.city} readonly />
          </IonItem>
        </IonCard>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Gebäude</IonCardTitle>
          </IonCardHeader>

          {building.groundFloor > 0 && <IonItem>
            <IonLabel position="floating">Erdgesschoss m<sup>2</sup></IonLabel>
            <IonInput value={building.groundFloor} readonly />
          </IonItem>}

          {building.firstFloor && building.firstFloor > 0 && <IonItem>
            <IonLabel position="floating">1. Obergeschoss m<sup>2</sup></IonLabel>
            <IonInput value={building.firstFloor} readonly />
          </IonItem>}

          {building.secondFloor && building.secondFloor > 0 && <IonItem>
            <IonLabel position="floating">2. Obergeschoss m<sup>2</sup></IonLabel>
            <IonInput value={building.secondFloor} readonly />
          </IonItem>}

          {building.mansarde && building.mansarde > 0 && <IonItem>
            <IonLabel position="floating">Mansarde m<sup>2</sup></IonLabel>
            <IonInput value={building.mansarde} readonly />
          </IonItem>}

          {building.basement && building.basement > 0 && <IonItem>
            <IonLabel position="floating">Keller m<sup>2</sup></IonLabel>
            <IonInput value={building.basement} readonly />
          </IonItem>}

          {additional.buildings && additional.buildings.map((b: any, index) => (
            <IonItem key={index}>
              <IonLabel position="floating">Versicherte Nebengebäude m<sup>2</sup></IonLabel>
              <IonInput value={`${b.name}, ${b.area}`} readonly />
            </IonItem>
          ))}
        </IonCard>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Zusätze</IonCardTitle>
          </IonCardHeader>

          {!additional.dogs && !additional.horses && additional.people && additional.people.length === 0 && <Note>
            Keine Zusätze
          </Note>}

          {additional.pool && <IonItem>
            <IonLabel position="floating">Pool</IonLabel>
            <IonInput value={'Ja'} readonly />
          </IonItem>}

          {additional.horses && additional.horses > 0 && <IonItem>
            <IonLabel position="floating">Pferde</IonLabel>
            <IonInput value={additional.horses} readonly />
          </IonItem>}

          {additional.dogs && additional.dogs > 0 && <IonItem>
            <IonLabel position="floating">Hunde</IonLabel>
            <IonInput value={additional.dogs} readonly />
          </IonItem>}

          {additional.people && additional.people.map((p: any) => (
            <IonItem key={p.id}>
              <IonLabel position="floating">Mitversicherte Person</IonLabel>
              <IonInput value={`${p.gender === 'm' ? 'Herr' : 'Frau'} ${p.firstName} ${p.lastName}, ${moment(p.birthday).format('YYYY-MM-DD')}`} readonly />
            </IonItem>
          ))}
        </IonCard>


      </IonContent>
    </IonPage>
  );
};

export default connect<StateProps, unknown, unknown, State>(
  ({history}, props: any) => {
    return {
      accessKey: history.list[props.match.params.id]?.accessKey || 'legacy',
    }
  },
  {
    addNotification
  },
)(ListEntry);

